import React, { useState, useEffect, useCallback } from "react";
import {
  PlusCircle,
  Edit,
  Trash2,
  Users,
  Loader2,
  Sparkles,
  Clock,
} from "lucide-react";

const API_BASE = "/api";

const TableModal = ({ table, onSave, onCancel, isDarkMode, floorPlanEnabled, zones }) => {
  const [formData, setFormData] = useState(table || {});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData({
      ...table,
      zone: table?.zone || "default",
      zone_id: table?.zone_id || zones?.[0]?.id || "",
      number: table?.number ?? "",
      name: table?.name ?? "",
      is_ephemeral: table?.is_ephemeral ?? 0,
    });
    setErrors({});
  }, [table, zones]);

  const isNewTable = !formData?.id;
  const isEphemeralTable = Boolean(formData?.is_ephemeral);
  const expirationDate =
    formData?.expires_at && new Date(formData.expires_at);
  const expirationLabel =
    expirationDate && !Number.isNaN(expirationDate.getTime())
      ? expirationDate.toLocaleString("fr-FR", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;

  const textMuted = isDarkMode ? "text-slate-400" : "text-slate-500";
  const cardClasses = isDarkMode
    ? "bg-[#0f172a] border border-white/10 text-slate-100"
    : "bg-white border border-slate-200 text-slate-900";
  const inputClasses = isDarkMode
    ? "bg-white/5 border border-white/10 text-slate-100 focus:border-amber-400 focus:ring-amber-400/30"
    : "bg-white border border-slate-300 text-slate-900 focus:border-amber-500 focus:ring-amber-200";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.number || formData.number < 1)
      newErrors.number = "Numéro de table requis (min 1)";
    if (!formData.capacity || formData.capacity < 1)
      newErrors.capacity = "Capacité requise (min 1)";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const selectedZone = (zones || []).find(
        (zone) => String(zone.id) === String(formData.zone_id),
      );
      onSave({
        ...formData,
        zone: floorPlanEnabled
          ? selectedZone?.name || formData.zone || "default"
          : "default",
        zone_id: floorPlanEnabled ? formData.zone_id || selectedZone?.id || null : null,
      });
    }
  };

  if (!table) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur">
      <div className={`${cardClasses} w-full max-w-md rounded-3xl p-6 shadow-2xl`}>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs uppercase tracking-[0.4em] text-amber-400`}>
                {formData.id ? "Modifier" : "Ajouter"}
              </p>
              <h2 className="text-2xl font-semibold">Table</h2>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-full border border-white/10 px-3 py-1 text-sm hover:border-amber-400/60"
            >
              Fermer
            </button>
          </div>

          {isEphemeralTable && (
            <div className="rounded-2xl border border-indigo-400/30 bg-indigo-500/10 px-4 py-3 text-xs text-indigo-100">
              Table éphémère
              {expirationLabel && (
                <span className="ml-1 text-indigo-200/80">
                  · Active jusqu'à {expirationLabel}
                </span>
              )}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className={`text-sm font-semibold ${textMuted}`}>
                Numéro de table *
              </label>
              <input
                name="number"
                type="number"
                value={formData?.number ?? ""}
                onChange={handleChange}
                placeholder="1"
                disabled={isNewTable}
                readOnly={isNewTable}
                className={`mt-1 w-full rounded-xl px-3 py-2 ${inputClasses} ${errors.number ? "border-red-500 focus:ring-red-500/30" : ""} ${isNewTable ? "opacity-70 cursor-not-allowed" : ""}`}
              />
              {errors.number && (
                <p className="text-xs text-red-400 mt-1">{errors.number}</p>
              )}
              {isNewTable && (
                <p className="text-xs mt-1 text-amber-400">
                  Numéro attribué automatiquement
                </p>
              )}
            </div>

            <div>
              <label className={`text-sm font-semibold ${textMuted}`}>
                Nom (optionnel)
              </label>
              <input
                name="name"
                value={formData?.name || ""}
                onChange={handleChange}
                placeholder="Table du coin..."
                className={`mt-1 w-full rounded-xl px-3 py-2 ${inputClasses}`}
              />
            </div>

            <div>
              <label className={`text-sm font-semibold ${textMuted}`}>
                Capacité *
              </label>
              <input
                name="capacity"
                type="number"
                value={formData.capacity || ""}
                onChange={handleChange}
                placeholder="4"
                className={`mt-1 w-full rounded-xl px-3 py-2 ${inputClasses} ${errors.capacity ? "border-red-500 focus:ring-red-500/30" : ""}`}
              />
              {errors.capacity && (
                <p className="text-xs text-red-400 mt-1">{errors.capacity}</p>
              )}
            </div>

            <div>
              <label className={`text-sm font-semibold ${textMuted}`}>
                Statut
              </label>
              <select
                name="status"
                value={formData.status || "free"}
                onChange={handleChange}
                className={`mt-1 w-full rounded-xl px-3 py-2 ${inputClasses}`}
              >
                <option value="free">Libre</option>
                <option value="occupied">Occupée</option>
                <option value="reserved">Réservée</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>

            {floorPlanEnabled && (
              <div>
                <label className={`text-sm font-semibold ${textMuted}`}>
                  Zone
                </label>
                <select
                  name="zone_id"
                  value={formData.zone_id || ""}
                  onChange={handleChange}
                  className={`mt-1 w-full rounded-xl px-3 py-2 ${inputClasses}`}
                >
                  {(zones || []).map((zone) => (
                    <option key={zone.id} value={zone.id} className="text-slate-900">
                      {zone.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <label className="flex items-center gap-2 text-sm font-semibold">
              <input
                type="checkbox"
                name="is_active"
                checked={Boolean(formData.is_active)}
                onChange={handleChange}
                className="rounded accent-amber-500"
              />
              <span className={textMuted}>Table active</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-xl border border-white/15 px-4 py-2 text-sm font-semibold hover:border-amber-400/60"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-2 text-sm font-semibold text-slate-900 shadow-lg hover:shadow-amber-500/40"
            >
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EphemeralTablesModal = ({
  isOpen,
  onClose,
  onCreate,
  isDarkMode,
  isLoading,
  nextNumber,
}) => {
  const [formData, setFormData] = useState({ count: 1, capacity: 4 });
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setFormData({ count: 1, capacity: 4 });
      setError("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const textMuted = isDarkMode ? "text-slate-400" : "text-slate-500";
  const cardClasses = isDarkMode
    ? "bg-[#0f172a] border border-white/10 text-slate-100"
    : "bg-white border border-slate-200 text-slate-900";
  const inputClasses = isDarkMode
    ? "bg-white/5 border border-white/10 text-slate-100 focus:border-indigo-400 focus:ring-indigo-400/30"
    : "bg-white border border-slate-300 text-slate-900 focus:border-indigo-500 focus:ring-indigo-200";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const parsedCount = parseInt(formData.count, 10);
    const parsedCapacity = parseInt(formData.capacity, 10);

    if (!Number.isInteger(parsedCount) || parsedCount < 1) {
      setError("Indiquez un nombre de tables valide (min. 1)");
      return;
    }
    if (parsedCount > 50) {
      setError("Limite de 50 tables éphémères par création.");
      return;
    }
    if (!Number.isInteger(parsedCapacity) || parsedCapacity < 1) {
      setError("La capacité minimale est de 1 couvert.");
      return;
    }

    onCreate({ count: parsedCount, capacity: parsedCapacity });
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur">
      <div className={`${cardClasses} w-full max-w-md rounded-3xl p-6 shadow-2xl`}>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-indigo-400">
                Tables éphémères
              </p>
              <h2 className="text-2xl font-semibold">Ajout rapide</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-white/10 px-3 py-1 text-sm hover:border-indigo-400/60"
            >
              Fermer
            </button>
          </div>

          <div className={`rounded-2xl border border-indigo-400/30 bg-indigo-500/10 px-4 py-3 text-sm ${textMuted}`}>
            Ces tables temporaires disparaissent automatiquement à la fin des
            horaires de service du jour.
          </div>

          <div className="space-y-4">
            <div>
              <label className={`text-sm font-semibold ${textMuted}`}>
                Nombre de tables *
              </label>
              <input
                type="number"
                name="count"
                min={1}
                value={formData.count}
                onChange={handleChange}
                className={`mt-1 w-full rounded-xl px-3 py-2 ${inputClasses}`}
              />
            </div>

            <div>
              <label className={`text-sm font-semibold ${textMuted}`}>
                Capacité (par table) *
              </label>
              <input
                type="number"
                name="capacity"
                min={1}
                value={formData.capacity}
                onChange={handleChange}
                className={`mt-1 w-full rounded-xl px-3 py-2 ${inputClasses}`}
              />
            </div>

            <p className={`text-xs ${textMuted}`}>
              La prochaine table portera le numéro{" "}
              <span className="font-semibold text-indigo-400">
                {nextNumber ?? "…"}
              </span>
              .
            </p>
          </div>

          {error && <p className="text-sm text-rose-400">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-white/15 px-4 py-2 text-sm font-semibold hover:border-indigo-400/60"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="rounded-xl bg-gradient-to-r from-indigo-400 to-violet-500 px-4 py-2 text-sm font-semibold text-slate-900 shadow-lg hover:shadow-indigo-500/40 disabled:opacity-70"
            >
              {isLoading ? "Création..." : "Créer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TableCard = ({
  table,
  onEdit,
  onDelete,
  onStatusChange,
  isDarkMode,
  showZone,
}) => {
  const textMuted = isDarkMode ? "text-slate-400" : "text-slate-500";
  const isEphemeral = Boolean(table.is_ephemeral);
  const baseCardClasses = isDarkMode
    ? "bg-white/5 border border-white/10 shadow-2xl shadow-black/40"
    : "bg-white border border-slate-200 shadow-lg shadow-slate-200/60";
  const cardClasses = `${baseCardClasses} ${
    isEphemeral
      ? isDarkMode
        ? "border-amber-400/50 shadow-amber-500/30"
        : "border-amber-300/70 shadow-amber-200/80"
      : ""
  }`;

  const statusChip = {
    free: "bg-emerald-500/10 text-emerald-500",
    occupied: "bg-rose-500/10 text-rose-500",
    reserved: "bg-amber-500/10 text-amber-500",
    maintenance: "bg-slate-500/10 text-slate-500",
  };

  const statusLabel = {
    free: "Libre",
    occupied: "Occupée",
    reserved: "Réservée",
    maintenance: "Maintenance",
  };

  const displayName =
    typeof table.name === "string" && table.name.trim().length
      ? table.name.trim()
      : null;
  const expirationDate =
    table.expires_at && new Date(table.expires_at);
  const formattedExpiration =
    isEphemeral && expirationDate && !Number.isNaN(expirationDate.getTime())
      ? expirationDate.toLocaleTimeString("fr-FR", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;

  return (
    <div className={`${cardClasses} rounded-3xl p-5 flex flex-col gap-4`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className={`text-xs uppercase tracking-[0.4em] ${textMuted}`}>
            Table
          </p>
          <h3 className="text-2xl font-semibold">{table.number}</h3>
          {displayName && (
            <p className={`text-sm ${textMuted} italic`}>{displayName}</p>
          )}
          {showZone && table.zone_name && (
            <p className={`text-xs ${textMuted}`}>Zone: {table.zone_name}</p>
          )}
          {isEphemeral && formattedExpiration && (
            <div className={`mt-2 flex items-center gap-1 text-xs ${textMuted}`}>
              <Clock size={14} />
              <span>Expire à {formattedExpiration}</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onEdit(table)}
            className="rounded-full border border-white/10 p-2 text-amber-400 hover:text-amber-300 hover:border-amber-400/60 transition"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => onDelete(table.id)}
            className="rounded-full border border-white/10 p-2 text-rose-400 hover:text-rose-300 hover:border-rose-400/60 transition"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="flex flex-wrap gap-2">
          <span
            className={`px-3 py-1 text-xs font-semibold rounded-full ${
              statusChip[table.status] || statusChip.free
            }`}
          >
            {statusLabel[table.status] || "Libre"}
          </span>
          {isEphemeral && (
            <span className="px-3 py-1 text-[11px] font-semibold rounded-full border border-indigo-400/40 bg-indigo-500/10 text-indigo-300">
              Éphémère
            </span>
          )}
        </div>
        <span className={`flex items-center gap-1 text-xs ${textMuted}`}>
          <Users size={14} />
          {table.capacity} pers.
        </span>
      </div>

      <select
        value={table.status}
        onChange={(e) => onStatusChange(table.id, e.target.value)}
        className={`rounded-2xl border border-white/15 px-3 py-2 text-sm bg-transparent ${textMuted}`}
      >
        {Object.keys(statusLabel).map((statusKey) => (
          <option key={statusKey} value={statusKey} className="text-slate-900">
            {statusLabel[statusKey]}
          </option>
        ))}
      </select>
    </div>
  );
};

const TableManagement = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingTable, setEditingTable] = useState(null);
  const [showEphemeralModal, setShowEphemeralModal] = useState(false);
  const [ephemeralLoading, setEphemeralLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterZoneId, setFilterZoneId] = useState("all");
  const [floorPlanEnabled, setFloorPlanEnabled] = useState(false);
  const [zones, setZones] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  );

  useEffect(() => {
    const target = document.documentElement;
    const syncTheme = () => setIsDarkMode(target.classList.contains("dark"));
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const containerBg = isDarkMode
    ? "bg-gradient-to-br from-[#050b18] via-[#0f1c33] to-[#050b18] text-slate-100"
    : "bg-gradient-to-br from-slate-50 via-white to-slate-100 text-slate-900";
  const cardClasses = isDarkMode
    ? "bg-white/5 border border-white/10 shadow-2xl shadow-black/40"
    : "bg-white border border-slate-100 shadow-xl shadow-slate-200/60";
  const textMuted = isDarkMode ? "text-slate-400" : "text-slate-500";

  const fetchTables = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/tables?raw=1`);
      if (!response.ok) throw new Error("Erreur réseau ou serveur");
      const data = await response.json();
      setTables(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  const fetchFloorPlan = useCallback(async () => {
    try {
      const [settingsRes, zonesRes] = await Promise.all([
        fetch(`${API_BASE}/settings/floor-plan?_ts=${Date.now()}`, { cache: "no-store" }),
        fetch(`${API_BASE}/zones?_ts=${Date.now()}`, { cache: "no-store" }),
      ]);
      if (settingsRes.ok) {
        const payload = await settingsRes.json();
        const enabled = Boolean(payload?.floor_plan_enabled);
        setFloorPlanEnabled(enabled);
        if (!enabled) {
          setZones([]);
          setFilterZoneId("all");
          return;
        }
      }
      if (zonesRes.ok) {
        const payload = await zonesRes.json();
        setZones(Array.isArray(payload) ? payload : []);
      }
    } catch (_error) {
      setFloorPlanEnabled(false);
      setZones([]);
    }
  }, []);

  useEffect(() => {
    fetchFloorPlan();
    const onFloorPlanUpdated = () => {
      fetchFloorPlan();
      fetchTables();
    };
    const onStorage = (event) => {
      if (
        event.key === "moo_floor_plan_enabled" ||
        event.key === "moo_floor_plan_broadcast"
      ) {
        onFloorPlanUpdated();
      }
    };
    window.addEventListener("moo-floor-plan-updated", onFloorPlanUpdated);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFloorPlanUpdated);
    return () => {
      window.removeEventListener("moo-floor-plan-updated", onFloorPlanUpdated);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFloorPlanUpdated);
    };
  }, [fetchFloorPlan, fetchTables]);

  const computeLocalNextNumber = () => {
    if (!tables.length) return 1;
    return (
      tables.reduce((max, current) => {
        const num = Number(current.number) || 0;
        return Math.max(max, num);
      }, 0) + 1
    );
  };

  const handleSaveTable = async (table) => {
    const url = table.id ? `${API_BASE}/tables/${table.id}` : `${API_BASE}/tables`;
    const method = table.id ? "PUT" : "POST";
    const payload = {
      ...table,
      number:
        table.number && Number(table.number) > 0
          ? Number(table.number)
          : undefined,
      name:
        typeof table.name === "string" && table.name.trim().length
          ? table.name.trim()
          : null,
      capacity:
        Number(table.capacity) > 0 ? Number(table.capacity) : 4,
      status: table.status || "free",
      is_active: table.is_active ? 1 : 0,
      is_ephemeral: table.is_ephemeral ? 1 : 0,
      zone: floorPlanEnabled ? table.zone || "default" : "default",
      zone_id: floorPlanEnabled && table.zone_id ? Number(table.zone_id) : null,
    };

    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Échec de la sauvegarde");
      }
      await fetchTables();
      setEditingTable(null);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette table ?")) {
      try {
        const response = await fetch(`${API_BASE}/tables/${tableId}`, {
          method: "DELETE",
        });
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.error || "Échec de la suppression");
        }
        await fetchTables();
      } catch (err) {
        alert(err.message);
      }
    }
  };

  const handleStatusChange = async (tableId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/tables/${tableId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || "Échec de la mise à jour");
      }
      await fetchTables();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleCreateEphemeralTables = async ({ count, capacity }) => {
    try {
      setEphemeralLoading(true);
      const response = await fetch(`${API_BASE}/tables/ephemeral`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ count, capacity }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Échec de la création des tables éphémères");
      }
      await fetchTables();
      setShowEphemeralModal(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setEphemeralLoading(false);
    }
  };

  const handleAddNew = async () => {
    let assignedNumber = computeLocalNextNumber();
    try {
      const response = await fetch(`${API_BASE}/tables/next-number`, {
        cache: "no-store",
      });
      if (response.ok) {
        const payload = await response.json();
        const serverNumber = Number(payload?.nextNumber);
        if (Number.isInteger(serverNumber) && serverNumber > 0) {
          assignedNumber = serverNumber;
        }
      }
    } catch (_error) {
      // L'aperçu local reste disponible hors connexion. Le serveur attribuera
      // dans tous les cas le numéro canonique lors de la sauvegarde.
    }
    setEditingTable({
      number: assignedNumber,
      name: "",
      capacity: 4,
      zone: floorPlanEnabled ? zones[0]?.name || "default" : "default",
      zone_id: floorPlanEnabled ? zones[0]?.id || null : null,
      status: "free",
      is_active: true,
      is_ephemeral: 0,
    });
  };

  const stats = {
    total: tables.length,
    free: tables.filter((t) => t.status === "free").length,
    occupied: tables.filter((t) => t.status === "occupied").length,
    reserved: tables.filter((t) => t.status === "reserved").length,
    maintenance: tables.filter((t) => t.status === "maintenance").length,
  };

  const sortedTables = [...tables].sort((a, b) => {
    const aNum = Number(a.number) || 0;
    const bNum = Number(b.number) || 0;
    return aNum - bNum;
  });

  const filteredByStatus =
    filterStatus === "all"
      ? sortedTables
      : sortedTables.filter((table) => table.status === filterStatus);

  const filteredTables =
    floorPlanEnabled && filterZoneId !== "all"
      ? filteredByStatus.filter((table) => String(table.zone_id || "") === String(filterZoneId))
      : filteredByStatus;

  return (
    <div className={`min-h-screen px-4 py-6 ${containerBg}`}>
      {editingTable && (
        <TableModal
          table={editingTable}
          onSave={handleSaveTable}
          onCancel={() => setEditingTable(null)}
          isDarkMode={isDarkMode}
          floorPlanEnabled={floorPlanEnabled}
          zones={zones}
        />
      )}
      <EphemeralTablesModal
        isOpen={showEphemeralModal}
        onClose={() => setShowEphemeralModal(false)}
        onCreate={handleCreateEphemeralTables}
        isDarkMode={isDarkMode}
        isLoading={ephemeralLoading}
        nextNumber={computeLocalNextNumber()}
      />

      <div className="max-w-7xl w-full mx-auto space-y-6">
        <header className={`${cardClasses} rounded-3xl p-6 space-y-3`}>
          <div className="flex flex-col gap-2">
            <p className={`text-xs uppercase tracking-[0.4em] ${textMuted}`}>
              Configuration des tables
            </p>
            <h1 className="text-3xl font-bold tracking-tight">Plan unique</h1>
            <p className={`text-sm ${textMuted}`}>
              Les établissements multi-zones (Salle/Terrasse/VIP...) seront pris en
              charge dans une prochaine version. Pour l'instant, MOO Sync fonctionne
              uniquement avec un plan unique de tables.
            </p>
            <p className={`text-xs ${textMuted}`}>
              Besoin de places supplémentaires ? Utilisez les tables éphémères pour
              ajouter des couvertures temporaires pendant la session courante.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleAddNew}
              className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-2 text-sm font-semibold text-slate-900 shadow-xl hover:shadow-amber-500/40"
            >
              <PlusCircle size={18} />
              Ajouter une table
            </button>
            <button
              onClick={() => setShowEphemeralModal(true)}
              className="inline-flex items-center gap-2 rounded-2xl border border-indigo-400/40 px-4 py-2 text-sm font-semibold text-indigo-200 hover:border-indigo-300/80"
            >
              <Sparkles size={18} />
              Tables éphémères
            </button>
          </div>
        </header>

        {floorPlanEnabled && zones.length > 0 && (
          <div className={`${cardClasses} rounded-3xl p-4`}>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setFilterZoneId("all")}
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                  filterZoneId === "all"
                    ? "bg-amber-400 text-slate-950"
                    : "border border-white/10 text-slate-300"
                }`}
              >
                Toutes les aires
              </button>
              {zones.map((zone) => (
                <button
                  key={zone.id}
                  type="button"
                  onClick={() => setFilterZoneId(zone.id)}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                    String(filterZoneId) === String(zone.id)
                      ? "bg-amber-400 text-slate-950"
                      : "border border-white/10 text-slate-300"
                  }`}
                >
                  {zone.name}
                  <span className="ml-2 opacity-60">
                    {Number(zone.table_count) || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-4">
          {[
            { label: "Toutes les tables", value: stats.total, status: "all" },
            { label: "Tables libres", value: stats.free, status: "free" },
            { label: "Occupées", value: stats.occupied, status: "occupied" },
            { label: "Maintenance", value: stats.maintenance, status: "maintenance" },
          ].map((card) => (
            <button
              key={card.label}
              onClick={() => setFilterStatus(card.status)}
              className={`${cardClasses} rounded-3xl p-4 text-left transition ${
                filterStatus === card.status ? "ring-2 ring-amber-400 shadow-amber-500/30" : ""
              }`}
            >
              <p className={`text-xs uppercase tracking-[0.3em] ${textMuted}`}>
                {card.label}
              </p>
              <p className="text-2xl font-semibold mt-2">{card.value}</p>
            </button>
          ))}
        </div>

        <div className={`${cardClasses} rounded-3xl`}>
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-3">
              <Loader2 className="animate-spin" size={20} />
              Chargement des tables...
            </div>
          ) : error ? (
            <p className="p-6 text-red-400">{error}</p>
          ) : filteredTables.length === 0 ? (
            <div className="p-6 text-center text-sm">
              Aucune table ne correspond à ce filtre pour le moment.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
              {filteredTables.map((table) => (
                <TableCard
                  key={table.id}
                  table={table}
                  onEdit={setEditingTable}
                  onDelete={handleDeleteTable}
                  onStatusChange={handleStatusChange}
                  isDarkMode={isDarkMode}
                  showZone={floorPlanEnabled}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TableManagement;
